import { useCallback, useMemo, useState } from 'react';

import {
  ChakraBox,
  ChakraVStack,
  FAIconName,
  FireflyButton,
  Popover,
} from '@equip.health/ui';

import FilterBuilder from './InquiryFilterBuilder';
import {
  ASFilterKeys,
  ASInitialFilterElementMapper,
  InquiryASFilterKeys,
  InquiryASFilterNameMapper,
  InquiryFilterConfig,
  InquiryFilterKeys,
  InquiryFilterTypes,
  InquiryFilterValue,
} from '~/lib/constants/inquiry.constants';
import {
  InquiryFilterDropdownMenuOptionsMapper,
  getFilterValues,
} from '~/lib/util/inquiry.util';
import { useResetFiltersOnDefaultChange } from '~/lib/hooks/useResetFiltersOnDefaultChange';

import './InquiryFilter.css';
import { useFlags } from 'launchdarkly-react-client-sdk';

const TOP_LEVEL_ID = 'inquiry-filter-popover';

const InquiryASFilter = ({
  admissionsSpecialists,
  admissionsOperationsCoordinators,
  targetTreatmentPrograms,
  channels,
  channelTypes,
  coverageStatusOptions,
  defaultFilters = [],
  isOpen,
  onClose,
  onSaveFilters,
  timezones,
  maxHeight,
  medicalStatusOptions,
  payors,
}: InquiryASFilterProps) => {
  const {
    inquiryListViewFilterByTreatmentProgram:
      isInquiryListViewFilterByTreatmentProgramEnabled,
  } = useFlags<LDFlags>();
  const [filterValues, setFilterValues] = useState<InquiryFilterValue[]>(
    () =>
      Object.values({ ...InquiryFilterKeys, ...ASFilterKeys }).reduce(
        (acc, key) => ({
          ...acc,
          [key]: ASInitialFilterElementMapper(defaultFilters)[key],
        }),
        {},
      ) as InquiryFilterValue[],
  );

  const onSubmitFilters = () => {
    onClose();
    const validFilters = Object.entries(filterValues).reduce(
      (acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value; // Only include defined filters
        }
        return acc;
      },
      {} as typeof filterValues,
    );

    onSaveFilters(
      Object.entries(validFilters).flatMap(([key, value]) =>
        getFilterValues(value, key as InquiryASFilterKeys),
      ),
    );
  };

  const onResetFilters = useCallback(() => {
    const initialFilterValues = Object.values({
      ...InquiryFilterKeys,
      ...ASFilterKeys,
    }).reduce((acc, key) => {
      const initialValue = ASInitialFilterElementMapper(defaultFilters)[key];
      const isMinMaxField =
        typeof initialValue === 'object' &&
        initialValue !== null &&
        'min' in initialValue &&
        'max' in initialValue;

      if (isMinMaxField) {
        acc[key] = {
          max: initialValue.max ?? '',
          min: initialValue.min ?? '',
        };
      } else {
        acc[key] = initialValue ?? '';
      }

      return acc;
    }, {}) as InquiryFilterValue[];

    setFilterValues(() => initialFilterValues);
  }, [defaultFilters]);

  useResetFiltersOnDefaultChange(defaultFilters, onResetFilters);

  // update filter values on input change
  const onFilterChange = (
    key: InquiryASFilterKeys,
    newValue: InquiryFilterValue,
  ): void => {
    if (newValue === undefined) return; // Skip undefined filters
    setFilterValues((prev) => ({
      ...prev,
      [key]: newValue,
    }));
  };

  const defaultFilterConfig = useCallback(
    (key: InquiryFilterKeys | ASFilterKeys): InquiryFilterConfig => {
      return {
        defaultValue: filterValues[key] ?? [],
        isSingleSelect: false,
        key,
        label: InquiryASFilterNameMapper[key],
        placeholder: '',
        type: InquiryFilterTypes.SELECT,
      };
    },
    [filterValues],
  );

  const ASFilterConfigs: InquiryFilterConfig[] = useMemo(
    () => [
      {
        ...defaultFilterConfig(InquiryFilterKeys.INQUIRY_STATUS),
        type: InquiryFilterTypes.STATUS,
      },
      {
        ...defaultFilterConfig(ASFilterKeys.AS_EXTERNAL_ID),
        providerFilterData: admissionsSpecialists,
        type: InquiryFilterTypes.PROVIDER,
      },
      {
        ...defaultFilterConfig(ASFilterKeys.AO_EXTERNAL_ID),
        providerFilterData: admissionsOperationsCoordinators,
        type: InquiryFilterTypes.PROVIDER,
      },
      {
        ...defaultFilterConfig(InquiryFilterKeys.LAST_CONTACT_DATE_MAX),
        label: 'Days since last contact',
        minMax: {
          max: {
            defaultValue: filterValues[InquiryFilterKeys.LAST_CONTACT_DATE_MAX],
            key: InquiryFilterKeys.LAST_CONTACT_DATE_MAX,
            label:
              InquiryASFilterNameMapper[
                InquiryFilterKeys.LAST_CONTACT_DATE_MAX
              ],
          },
          min: {
            defaultValue: filterValues[InquiryFilterKeys.LAST_CONTACT_DATE_MIN],
            key: InquiryFilterKeys.LAST_CONTACT_DATE_MIN,
            label:
              InquiryASFilterNameMapper[
                InquiryFilterKeys.LAST_CONTACT_DATE_MIN
              ],
          },
        },
        type: InquiryFilterTypes.MIN_MAX,
      },
      {
        ...defaultFilterConfig(ASFilterKeys.NEXT_CONTACT_DATE_MIN),
        label: 'Next contact',
        minMax: {
          max: {
            defaultValue: filterValues[ASFilterKeys.NEXT_CONTACT_DATE_MAX],
            key: ASFilterKeys.NEXT_CONTACT_DATE_MAX,
            label:
              InquiryASFilterNameMapper[ASFilterKeys.NEXT_CONTACT_DATE_MAX],
          },
          min: {
            defaultValue: filterValues[ASFilterKeys.NEXT_CONTACT_DATE_MIN],
            key: ASFilterKeys.NEXT_CONTACT_DATE_MIN,
            label:
              InquiryASFilterNameMapper[ASFilterKeys.NEXT_CONTACT_DATE_MIN],
          },
        },
        type: InquiryFilterTypes.DATE_RANGE,
      },
      {
        ...defaultFilterConfig(ASFilterKeys.TARGET_ADMIT_DATE_MIN),
        label: 'Target admit date',
        minMax: {
          max: {
            defaultValue: filterValues[ASFilterKeys.TARGET_ADMIT_DATE_MAX],
            key: ASFilterKeys.TARGET_ADMIT_DATE_MAX,
            label:
              InquiryASFilterNameMapper[ASFilterKeys.TARGET_ADMIT_DATE_MAX],
          },
          min: {
            defaultValue: filterValues[ASFilterKeys.TARGET_ADMIT_DATE_MIN],
            key: ASFilterKeys.TARGET_ADMIT_DATE_MIN,
            label:
              InquiryASFilterNameMapper[ASFilterKeys.TARGET_ADMIT_DATE_MIN],
          },
        },
        type: InquiryFilterTypes.DATE_RANGE,
      },
      ...(isInquiryListViewFilterByTreatmentProgramEnabled
        ? [
            {
              ...defaultFilterConfig(
                InquiryFilterKeys.TARGET_TREATMENT_PROGRAM,
              ),
              options: InquiryFilterDropdownMenuOptionsMapper(
                targetTreatmentPrograms,
              ),
              type: InquiryFilterTypes.SELECT,
            },
          ]
        : []),
      {
        ...defaultFilterConfig(InquiryFilterKeys.TIMEZONE),
        options: InquiryFilterDropdownMenuOptionsMapper(timezones),
        type: InquiryFilterTypes.SELECT,
      },
      {
        ...defaultFilterConfig(ASFilterKeys.INSURANCE_COMPANY),
        options: payors?.map((option) => ({
          label: option.label,
          value: option.label,
        })),
        type: InquiryFilterTypes.SELECT,
      },
      {
        ...defaultFilterConfig(ASFilterKeys.INSURANCE_REVIEW_STATUS),
        options: coverageStatusOptions,
        type: InquiryFilterTypes.SELECT,
      },
      {
        ...defaultFilterConfig(ASFilterKeys.MEDICAL_REVIEW_STATUS),
        options: medicalStatusOptions,
        type: InquiryFilterTypes.SELECT,
      },
      {
        ...defaultFilterConfig(ASFilterKeys.MEDICAL_APPOINTMENT_START_DATE),
        label: 'Medical appointment',
        minMax: {
          max: {
            defaultValue:
              filterValues[ASFilterKeys.MEDICAL_APPOINTMENT_END_DATE],
            key: ASFilterKeys.MEDICAL_APPOINTMENT_END_DATE,
            label:
              InquiryASFilterNameMapper[
                ASFilterKeys.MEDICAL_APPOINTMENT_END_DATE
              ],
          },
          min: {
            defaultValue:
              filterValues[ASFilterKeys.MEDICAL_APPOINTMENT_START_DATE],
            key: ASFilterKeys.MEDICAL_APPOINTMENT_START_DATE,
            label:
              InquiryASFilterNameMapper[
                ASFilterKeys.MEDICAL_APPOINTMENT_START_DATE
              ],
          },
        },
        type: InquiryFilterTypes.DATE_RANGE,
      },
      {
        ...defaultFilterConfig(InquiryFilterKeys.CHANNEL),
        options: channels.map((option) => ({
          color: option.color,
          label: option.channel,
          value: option.channel,
        })),
        type: InquiryFilterTypes.SELECT,
      },
      {
        ...defaultFilterConfig(InquiryFilterKeys.CHANNEL_TYPE),
        options: InquiryFilterDropdownMenuOptionsMapper(channelTypes),
        type: InquiryFilterTypes.SELECT,
      },

      {
        ...defaultFilterConfig(InquiryFilterKeys.INQUIRY_DATE_MIN),
        minMax: {
          max: {
            defaultValue: filterValues[InquiryFilterKeys.INQUIRY_DATE_MAX],
            key: InquiryFilterKeys.INQUIRY_DATE_MAX,
            label:
              InquiryASFilterNameMapper[InquiryFilterKeys.INQUIRY_DATE_MAX],
          },
          min: {
            defaultValue: filterValues[InquiryFilterKeys.INQUIRY_DATE_MIN],
            key: InquiryFilterKeys.INQUIRY_DATE_MIN,
            label:
              InquiryASFilterNameMapper[InquiryFilterKeys.INQUIRY_DATE_MIN],
          },
        },
        type: InquiryFilterTypes.DATE_RANGE,
      },
    ],
    [
      admissionsOperationsCoordinators,
      admissionsSpecialists,
      channelTypes,
      channels,
      coverageStatusOptions,
      defaultFilterConfig,
      filterValues,
      medicalStatusOptions,
      payors,
      targetTreatmentPrograms,
      timezones,
    ],
  );

  return (
    <Popover
      bodyContent={
        <ChakraVStack>
          <FilterBuilder
            filters={ASFilterConfigs}
            id={TOP_LEVEL_ID}
            onChange={onFilterChange}
          />
          <ChakraBox
            backgroundColor="white"
            bottom="0"
            paddingLeft="20px"
            paddingRight="20px"
            position="sticky"
            width="100%"
          >
            <FireflyButton
              id={`${TOP_LEVEL_ID}__save-filters`}
              isFullWidth
              leftIcon={FAIconName.checkRegular}
              onClick={onSubmitFilters}
            >
              Save filters
            </FireflyButton>
          </ChakraBox>
        </ChakraVStack>
      }
      // We specifically want content padding on the bottom to prevent the scroll to appear below the Save button.
      bodyPadding="20px 0px 0px 0px"
      contentPadding="0px 0px 20px 0px"
      id={TOP_LEVEL_ID}
      isOpen={isOpen}
      maxHeight={maxHeight ?? 'calc(100vh - 75px)'}
      minWidth="360px"
      onClose={() => {
        onResetFilters();
        onClose();
      }}
      size="md"
    />
  );
};

export default InquiryASFilter;
